import { useEffect } from "react";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import type { ClubSummary } from "@pagemates/shared";
import { api, describeError } from "../lib/api";
import { Wordmark } from "../components/Wordmark";
import { WrongDoorNote } from "../components/SignIn";
import { looksLikeWrongDoor } from "../lib/auth";
import { Avatar, Empty, Spinner } from "../components/ui";
import { CalendarIcon, PlusIcon, TalkIcon } from "../components/Icons";
import { Bell } from "../components/Bell";

const DAY = 86_400_000;
function countdown(iso: string): string {
  const days = Math.ceil((new Date(iso).getTime() - Date.now()) / DAY);
  if (days < 0) return "passed";
  if (days === 0) return "today";
  if (days === 1) return "tomorrow";
  return `in ${days} days`;
}

export function Clubs() {
  const me = useQuery({ queryKey: ["me"], queryFn: api.me });
  const navigate = useNavigate();
  const { all } = useSearch({ from: "/authed/clubs" });
  const atLimit = (me.data?.clubs.filter((c) => c.role === "owner").length ?? 0) >= 5;

  // One club: go straight in. "All clubs" and the back arrow pass ?all to land here.
  useEffect(() => {
    if (!all && me.data?.clubs.length === 1) void navigate({ to: "/clubs/$clubId", params: { clubId: me.data.clubs[0]!.slug }, replace: true });
  }, [me.data, all, navigate]);

  return (
    <main className="page clubs stack">
      <header className="topbar">
        <Wordmark size="md" />
        <div className="row" style={{ gap: 6 }}>
          <Bell />
          {me.data && <Link to="/me" className="row" aria-label="Your profile"><Avatar name={me.data.displayName ?? me.data.name} image={me.data.image} /></Link>}
        </div>
      </header>
      {me.isPending && <Spinner />}
      {me.isError && <p className="error">{describeError(me.error)}</p>}
      {me.data && (me.data.clubs.length === 0 ? (
        <>
          {looksLikeWrongDoor(me.data) && <WrongDoorNote me={me.data} />}
          <Empty title="No clubs yet">Start one for your group, or open an invite link a friend sent you.</Empty>
        </>
      ) : (
        <div className="club-grid">
          {me.data.clubs.map((c) => <ClubCard key={c.id} c={c} />)}
        </div>
      ))}
      {me.data && (
        <div className="start-club">
          {/* The five-club limit is checked here, before the flow opens, not at its end. */}
          {atLimit
            ? <p className="muted small" style={{ margin: 0 }}>You run five clubs, the most one person can own. To start another, hand one to another member or delete one from its Club settings.</p>
            : <button type="button" className="btn primary" onClick={() => void navigate({ to: "/clubs/new" })}><PlusIcon width={16} height={16} />Start a club</button>}
          <Link to="/clubs/browse" className="find-link">Find a club to join</Link>
          <p className="muted small" style={{ margin: 0 }}>Invited to one? Open the link you were sent.</p>
        </div>
      )}
    </main>
  );
}

function ClubCard({ c }: { c: ClubSummary }) {
  const b = c.currentBook;
  return (
    <Link to="/clubs/$clubId" params={{ clubId: c.slug }} className="club-card">
      <div className="cc-cover">{b?.coverUrl ? <img src={b.coverUrl} alt="" loading="lazy" /> : <span className="cc-blank" />}</div>
      <div className="cc-body">
        <div className="cc-name">{c.name}</div>
        {b ? (
          <>
            <div className="cc-book">{b.title}</div>
            <div className="bar" style={{ marginTop: 8 }}><i style={{ width: `${b.myProgress}%` }} /></div>
            <div className="cc-line"><span>{b.myProgress >= 100 ? "finished" : `you're at ${b.myProgress}%`}</span></div>
          </>
        ) : (
          <div className="cc-book muted">{c.onDeckCount > 0 ? `Nothing started · ${c.onDeckCount} on deck` : "Nothing on the nightstand yet"}</div>
        )}
        <div className="cc-foot">
          <span className="avs">{c.members.slice(0, 5).map((m) => <Avatar key={m.id} name={m.name} image={m.image} short />)}{c.memberCount > 5 && <span className="more-n">+{c.memberCount - 5}</span>}</span>
          {c.meetingAt && <span className="cc-chip"><CalendarIcon width={14} height={14} />{countdown(c.meetingAt)}</span>}
          {c.unreadMessages > 0 && <span className="cc-chip accent"><TalkIcon width={14} height={14} />{c.unreadMessages} new</span>}
        </div>
      </div>
    </Link>
  );
}

